import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Buildings, Flag, Calendar, Clock, Users, Bed, MapPin, X, Plus } from "@phosphor-icons/react";

export default function MyBookings() {
  const [tee, setTee] = useState([]);
  const [resorts, setResorts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [t, r] = await Promise.all([
        api.get("/bookings/tee-time"),
        api.get("/bookings/resort"),
      ]);
      setTee(t.data);
      setResorts(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const cancelTee = async (b) => {
    if (!window.confirm(`Cancel tee time at ${b.course_name} on ${b.tee_date}?`)) return;
    try {
      await api.delete(`/bookings/tee-time/${b.id}`);
      toast.success("Tee time cancelled");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const cancelResort = async (b) => {
    if (!window.confirm(`Cancel stay at ${b.resort_name}?`)) return;
    try {
      await api.delete(`/bookings/resort/${b.id}`);
      toast.success("Resort booking cancelled");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-8">
      <div className="rise flex items-end justify-between gap-3 flex-wrap">
        <div>
          <span className="label-eyebrow">My trip</span>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">My bookings</h1>
          <p className="text-[#0F2537]/70 mt-2">Tee times and resort stays you've locked in.</p>
        </div>
        <Link to="/booking" data-testid="add-booking-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#E05D3A] text-white text-sm font-semibold hover:brightness-110 transition">
          <Plus size={16} weight="bold" /> New booking
        </Link>
      </div>

      <Section title="Tee times" icon={Flag} count={tee.length}>
        {!loading && tee.length === 0 && <Empty cta="Book a tee time" link="/booking?tab=tee-times" />}
        <div className="grid md:grid-cols-2 gap-4">
          {tee.map((b) => (
            <article key={b.id} data-testid={`tee-booking-${b.id}`} className="card-default p-5">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-1 text-xs font-semibold text-[#0F2537]/60"><MapPin size={11} weight="fill" /> Course</div>
                  <h3 className="font-heading font-bold text-lg truncate">{b.course_name}</h3>
                </div>
                <StatusPill status={b.status} />
              </div>
              <div className="grid grid-cols-3 gap-3 mt-4">
                <Cell icon={Calendar} label="Date" value={b.tee_date} />
                <Cell icon={Clock} label="Tee off" value={b.slot} />
                <Cell icon={Users} label="Players" value={String(b.players)} />
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#E2E0D8]">
                <span className="font-heading text-xl font-black">FJ${b.total_fjd.toLocaleString()}</span>
                {b.status === "confirmed" && (
                  <button onClick={() => cancelTee(b)} data-testid={`cancel-tee-${b.id}`} className="inline-flex items-center gap-1 text-xs font-bold text-[#0F2537]/70 hover:text-red-600">
                    <X size={12} weight="bold" /> Cancel
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      </Section>

      <Section title="Resort stays" icon={Buildings} count={resorts.length}>
        {!loading && resorts.length === 0 && <Empty cta="Browse resorts" link="/booking?tab=resorts" />}
        <div className="grid md:grid-cols-2 gap-4">
          {resorts.map((b) => (
            <article key={b.id} data-testid={`resort-booking-${b.id}`} className="card-default p-5">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-1 text-xs font-semibold text-[#0F2537]/60"><MapPin size={11} weight="fill" /> Resort</div>
                  <h3 className="font-heading font-bold text-lg truncate">{b.resort_name}</h3>
                </div>
                <StatusPill status={b.status} />
              </div>
              <div className="grid grid-cols-4 gap-3 mt-4">
                <Cell icon={Calendar} label="In" value={b.check_in} />
                <Cell icon={Calendar} label="Out" value={b.check_out} />
                <Cell icon={Bed} label={b.room_category} value={`${b.rooms} rm`} />
                <Cell icon={Users} label="Guests" value={String(b.guests)} />
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#E2E0D8]">
                <span className="font-heading text-xl font-black">FJ${b.total_fjd.toLocaleString()}<span className="text-xs font-semibold text-[#0F2537]/60"> · {b.nights} nights</span></span>
                {b.status === "confirmed" && (
                  <button onClick={() => cancelResort(b)} data-testid={`cancel-resort-${b.id}`} className="inline-flex items-center gap-1 text-xs font-bold text-[#0F2537]/70 hover:text-red-600">
                    <X size={12} weight="bold" /> Cancel
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      </Section>
    </div>
  );
}

function Section({ title, icon: Icon, count, children }) {
  return (
    <section className="rise space-y-3">
      <h2 className="flex items-center gap-2 font-heading text-2xl font-black">
        <Icon size={20} weight="fill" className="text-[#E05D3A]" /> {title}
        <span className="text-sm font-bold text-[#0F2537]/40">({count})</span>
      </h2>
      {children}
    </section>
  );
}
function Cell({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#0F2537]/50"><Icon size={11} weight="fill" /> {label}</div>
      <div className="text-sm font-bold mt-0.5">{value}</div>
    </div>
  );
}
function StatusPill({ status }) {
  const cls = status === "confirmed"
    ? "bg-[#1E5631]/10 text-[#1E5631]" : "bg-black/10 text-[#0F2537]/60";
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${cls}`}>{status}</span>;
}
function Empty({ cta, link }) {
  return (
    <div className="card-default p-8 text-center">
      <p className="text-sm text-[#0F2537]/60">No bookings yet.</p>
      <Link to={link} className="inline-flex mt-3 items-center gap-1 px-3 py-2 rounded-lg bg-[#0F2537] text-white text-xs font-bold hover:bg-[#0F2537]/90">
        {cta}
      </Link>
    </div>
  );
}
